import { type CoreUIAppModule, CoreUIProvider, type PrimaryThemeName } from '@eleo-rh/core-ui'
import { ModalProvider } from '@eleo-rh/core-ui/contexts'
import { App, ConfigProvider } from 'antd'
import type { Locale } from 'antd/es/locale'
import deDE from 'antd/locale/de_DE'
import enGB from 'antd/locale/en_GB'
import esES from 'antd/locale/es_ES'
import fiFI from 'antd/locale/fi_FI'
import frFR from 'antd/locale/fr_FR'
import itIT from 'antd/locale/it_IT'
import jaJP from 'antd/locale/ja_JP'
import koKR from 'antd/locale/ko_KR'
import nlNL from 'antd/locale/nl_NL'
import ptPT from 'antd/locale/pt_PT'
import zhCN from 'antd/locale/zh_CN'
import zhTW from 'antd/locale/zh_TW'
import { useI18n } from 'context/I18nProvider'
import { useQuery } from 'context/QueryProvider'
import ToastProvider from 'context/ToastProvider'
import { type PropsWithChildren, useEffect, useState } from 'react'
import SettingsSingleton from 'services/SettingsSingleton'

const langMap = {
  de: 'de',
  en: 'en-gb',
  es: 'es',
  fr: 'fr',
} as const

const antdLocales: Record<string, Locale> = {
  de: deDE,
  en: enGB,
  es: esES,
  fi: fiFI,
  fr: frFR,
  it: itIT,
  ja: jaJP,
  ko: koKR,
  nl: nlNL,
  pt: ptPT,
  zh_Hans: zhCN,
  zh_Hant: zhTW,
}

export default function CoreUIProviderAdapter({ children }: PropsWithChildren) {
  const { i18n, locale } = useI18n()
  const { client } = useQuery()
  const [mainColor, setMainColor] = useState<PrimaryThemeName | null>()
  const [module, setModule] = useState<CoreUIAppModule>('training')

  useEffect(() => {
    void SettingsSingleton.getInstance()
      .getSettings(client)
      .then(settings => {
        setMainColor(settings?.mainColor ?? null)
        setModule(settings?.safetyStandalone ? 'safety' : 'eleo')
      })
  }, [client])

  return (
    <CoreUIProvider
      lang={langMap[locale as keyof typeof langMap] ?? 'fr'}
      module={module}
      primaryTheme={mainColor}
      onPrimaryThemeChange={setMainColor}
      form={{ validateMessages: { required: i18n('required_field') } }}
      theme={{
        components: {
          Listy: {
            itemPaddingBlock: 24,
            controlItemBgHover: 'var(--color-slate-50)',
          },
        },
      }}
    >
      <ConfigProvider locale={antdLocales[locale] ?? frFR}>
        <App>
          <ToastProvider>
            <ModalProvider>{children}</ModalProvider>
          </ToastProvider>
        </App>
      </ConfigProvider>
    </CoreUIProvider>
  )
}
